import { resources } from "@constants/hardcoded-strings";
import { LANGUAGE_SELECT_OPTIONS } from "@constants/language-select-options";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { AddMessageTemplateLanguageModal } from "@containers/modals/add-message-template-language-modal/add-message-template-language-modal";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { MessageTemplateVariablesValidationErrors } from "@idw-types/common/validation/message-template/message-template-validation-errors";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import { getMessageTemplateVariables } from "@utils/message-template/message-template-util";
import { convertDotSeparatedStringToJSONObject } from "@utils/string-util";
import merge from "lodash/merge";
import { ChangeEvent, Fragment, useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import commonStyles from "../../message-template-details-component.less";
import { MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME } from "@constants/message-template-constants";
import { MessageTemplateTypes } from "@enums/message-template-types";

type MessageTemplateVariablesFormSectionProps = {
    state: FormattedMessageTemplate;
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    selectedLanguage: LocaleTypes;
    validationErrors: MessageTemplateVariablesValidationErrors;
    setSelectedLanguage: (language: LocaleTypes) => void;
    setMessageTemplateState: (state: FormattedMessageTemplate) => void;
};

export const MessageTemplateVariablesFormSection = (props: MessageTemplateVariablesFormSectionProps) => {
    const {
        state,
        labelWidth,
        isDisabled,
        selectedLanguage,
        validationErrors,
        setSelectedLanguage,
        setMessageTemplateState
    } = props;

    const resource = resources.pages.messageTemplateDetails.tabs.variables;

    const modalContext = useModalContext();

    const {
        selectedLanguages,
        availableLanguages
    } = useMemo(() => {
        const selectedLanguages = Object.keys(state.languages) as Array<LocaleTypes>;
        const availableLanguages = LANGUAGE_SELECT_OPTIONS.filter(lso => !selectedLanguages.includes(lso.value));

        return {
            selectedLanguages,
            availableLanguages
        };
    }, [state.languages]);

    const variables = useMemo(() => {
        return getMessageTemplateVariables(state.languages, state.template, selectedLanguage);
    }, [state.languages, state.template, selectedLanguage]);

    const handleUpdateLanguage = (newOption: SelectOption<LocaleTypes>) => {
        setSelectedLanguage(newOption.value);
    };

    const handleUpdateEmailSubject = (event: ChangeEvent<HTMLInputElement>) => {
        const newJSONObject = convertDotSeparatedStringToJSONObject(MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME, event.target.value);

        const newLanguageFile = merge({}, state.languages[selectedLanguage], newJSONObject);
        setMessageTemplateState({
            ...state,
            languages: {
                ...state.languages,
                [selectedLanguage]: newLanguageFile
            }
        });
    };

    const handleChangeAtIndex = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const newJSONObject = convertDotSeparatedStringToJSONObject(variables[index].name, event.target.value);
        const newLanguageFile = merge({}, state.languages[selectedLanguage], newJSONObject);

        setMessageTemplateState({
            ...state,
            languages: {
                ...state.languages,
                [selectedLanguage]: newLanguageFile
            }
        });
    };


    const onClickContinue = (localeType: LocaleTypes) => {
        setMessageTemplateState({
            ...state,
            languages: {
                ...state.languages,
                [localeType]: state.languages[LocaleTypes.ENGLISH]
            }
        });
        setSelectedLanguage(localeType);
    };

    const onClickAddLanguage = () => {
        const renderAddMessageTemplateLanguageModal = (onModalClose: () => void) => {
            return (
                <AddMessageTemplateLanguageModal
                    availableLanguages={availableLanguages}
                    onClickContinue={onClickContinue}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddMessageTemplateLanguage,
            renderFunction: renderAddMessageTemplateLanguageModal
        });
    };

    const languageOptions = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.filter(lso => selectedLanguages.includes(lso.value));
    }, [state.languages, selectedLanguages]);

    const selectedLanguageOption = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.find(lso => lso.value === selectedLanguage) || null;
    }, [state.languages, selectedLanguage]);

    const {
        languageValidationErrors
    }: { languageValidationErrors: { [id: string]: React.ReactNode }; count: number; } = useMemo(() => {
        let errors = {};
        let count = 0;
        if (validationErrors?.multiLanguageValidationErrors[selectedLanguage]) {
            const data = validationErrors.multiLanguageValidationErrors[selectedLanguage];
            errors = data.errors || {};
            count = data.count;
        }
        return {
            languageValidationErrors: errors,
            count
        };
    }, [validationErrors, selectedLanguage]);


    return (
        <ThemingFormSection title={resource.sections.variables.title}>
            <div className={commonStyles.sectionDescription}>{resource.sections.variables.description}</div>
            <SelectFormGroup
                controlId={`message-template-language-select`}
                automationId={`message-template-language-select`}
                ariaLabel={resource.inputs.language.label}
                disabled={false}
                isClearable={false}
                isRequired={true}
                isSearchable={true}
                selectedOption={selectedLanguageOption}
                options={languageOptions}
                onChange={handleUpdateLanguage}
            />
            {!isDisabled && availableLanguages.length > 0 && (
                <Button onClick={onClickAddLanguage}>
                    {resource.buttons.addLanguage.label}
                </Button>
            )}
            <hr />
            <>
                {state.type === MessageTemplateTypes.EMAIL && (
                    <>
                        <TextareaFormGroup
                            controlId={`message-template-subject-variable`}
                            automationId={`message-template-subject-variable`}
                            label={MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME}
                            ariaLabel={MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME}
                            isRequired={true}
                            onChange={handleUpdateEmailSubject}
                            value={state.languages[selectedLanguage]?.[MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME] || ""}
                            disabled={isDisabled}
                            labelWidth={labelWidth}
                            layout={LayoutTypes.COLUMN}
                        />
                        {languageValidationErrors[MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME]}
                    </>
                )}
                {variables.map((variable, index) => (
                    <Fragment key={`message-template-variable-${variable.name}`}>
                        <TextareaFormGroup
                            controlId={`message-template-variable-${variable.name}`}
                            automationId={`message-template-variable-${variable.name}`}
                            label={variable.name}
                            ariaLabel={variable.name}
                            isRequired={true}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleChangeAtIndex(event, index)}
                            value={variable.value}
                            disabled={isDisabled}
                            labelWidth={labelWidth}
                            layout={LayoutTypes.COLUMN}
                        />
                        {languageValidationErrors[variable.name] || null}
                    </Fragment>
                ))}
            </>
            {variables.length === 0 && (
                <Alert>{resource.noVariablesMessage}</Alert>
            )}
        </ThemingFormSection>
    );
};